import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSite } from "../lib/SiteContext";
import api from "../lib/api";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";
import CsvImportModal from "../components/CsvImportModal";
import { ConfidencePill } from "../components/Pills";
import LivePowerCard from "../components/dashboard/LivePowerCard";
import KpiCard from "../components/KpiCard";
import {
  Activity, ArrowDownToLine, ArrowUpFromLine, Sun, Upload, Zap,
} from "lucide-react";
import { fmt, shortDate } from "../lib/format";

const TOOLTIP_STYLE = {
  background: "#18181B",
  border: "1px solid #27272A",
  borderRadius: 8,
  fontSize: 12,
};

const PERIODS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "month", label: "This month" },
  { id: "year", label: "This year" },
];

/** Pivot a 365-day daily series into year-month totals for the monthly chart. */
function rollupByMonth(rows) {
  const buckets = new Map();
  for (const r of rows) {
    if (!r.date) continue;
    const month = r.date.slice(0, 7); // YYYY-MM
    if (!buckets.has(month)) {
      buckets.set(month, { month, import: 0, export: 0, pv: 0, days: 0 });
    }
    const b = buckets.get(month);
    if (r.grid_import_kwh != null) b.import += Number(r.grid_import_kwh);
    if (r.grid_export_kwh != null) b.export += Number(r.grid_export_kwh);
    if (r.pv_production_kwh != null) b.pv += Number(r.pv_production_kwh);
    if (r.grid_import_kwh != null || r.grid_export_kwh != null) b.days += 1;
  }
  return [...buckets.values()]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((b) => ({
      label: new Date(`${b.month}-01`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" }),
      ...b,
      import: Number(b.import.toFixed(1)),
      export: Number(b.export.toFixed(1)),
      pv: Number(b.pv.toFixed(1)),
    }));
}

function PeriodTabs({ value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="energy-period-tabs">
      {PERIODS.map((p) => (
        <button
          key={p.id}
          onClick={() => onChange(p.id)}
          data-testid={`energy-period-${p.id}`}
          className={`px-3 py-1.5 transition-colors ${
            value === p.id
              ? "bg-foreground text-background"
              : "bg-transparent text-muted-foreground hover:bg-secondary"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export default function Energy() {
  const { currentSite } = useSite();
  const [rows, setRows] = useState([]);
  const [yearRows, setYearRows] = useState([]);
  const [period, setPeriod] = useState("today");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openImport, setOpenImport] = useState(null);

  const reload = useCallback(async () => {
    if (!currentSite) return;
    setLoading(true);
    const [dailyRes, yearRes] = await Promise.all([
      api.get(`/sites/${currentSite.id}/energy/daily?days=60`),
      api.get(`/sites/${currentSite.id}/energy/daily?days=365`),
    ]);
    setRows(dailyRes.data.rows || []);
    setYearRows(yearRes.data.rows || []);
    setLoading(false);
  }, [currentSite]);

  const reloadSummary = useCallback(async () => {
    if (!currentSite) return;
    const { data } = await api.get(
      `/sites/${currentSite.id}/energy/summary?period=${period}`,
    );
    setSummary(data);
  }, [currentSite, period]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadSummary(); }, [reloadSummary]);

  const chartRows = useMemo(
    () =>
      rows.map((r) => ({
        date: r.date,
        label: shortDate(r.date),
        pv: r.pv_production_kwh,
        import: r.grid_import_kwh,
        export: r.grid_export_kwh,
        consumption: r.site_consumption_kwh,
      })),
    [rows],
  );

  const monthlyRows = useMemo(() => rollupByMonth(yearRows), [yearRows]);

  return (
    <div className="space-y-8 pb-12" data-testid="energy-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="label-tiny mb-1.5 flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-[#F59E0B]" /> Energy
          </div>
          <h1 className="font-heading text-3xl font-semibold">Energy log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live power · import / export · daily and monthly aggregates
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setOpenImport("p1")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-xs hover:bg-secondary" data-testid="energy-import-p1">
            <Upload className="w-3.5 h-3.5" /> Import P1 CSV
          </button>
          <button onClick={() => setOpenImport("pv")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-xs hover:bg-secondary" data-testid="energy-import-pv">
            <Sun className="w-3.5 h-3.5" /> Import PV CSV
          </button>
        </div>
      </header>

      {/* Live snapshot */}
      <LivePowerCard siteId={currentSite?.id} />

      {/* Period KPI strip */}
      <section className="space-y-3" data-testid="energy-period-summary">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <h2 className="label-tiny flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-foreground" /> Period summary
            </h2>
            {summary?.days_with_data != null && (
              <span className="text-[11px] text-muted-foreground">
                {summary.days_with_data} day{summary.days_with_data === 1 ? "" : "s"} of data
              </span>
            )}
          </div>
          <PeriodTabs value={period} onChange={setPeriod} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard
            label="Import"
            value={fmt(summary?.import_kwh, 2)}
            unit="kWh"
            Icon={ArrowDownToLine}
            accent="red"
            testid="energy-summary-import"
          />
          <KpiCard
            label="Export"
            value={fmt(summary?.export_kwh, 2)}
            unit="kWh"
            Icon={ArrowUpFromLine}
            accent="green"
            testid="energy-summary-export"
          />
          <KpiCard
            label="Net"
            value={fmt(summary?.net_kwh, 2)}
            unit="kWh"
            Icon={Activity}
            accent={
              summary && summary.net_kwh != null && summary.net_kwh <= 0 ? "green" : "default"
            }
            sub={summary?.net_kwh != null && summary.net_kwh <= 0 ? "Net exporter" : "Net importer"}
            testid="energy-summary-net"
          />
        </div>
      </section>

      {/* Daily chart */}
      <section className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="label-tiny">Daily kWh — last 60 days</div>
          <div className="flex gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59E0B]"/>PV</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#FAFAFA]"/>Consumption</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#E11D48]"/>Import</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]"/>Export</span>
          </div>
        </div>
        <div className="h-72" data-testid="energy-daily-chart">
          <ResponsiveContainer>
            <LineChart data={chartRows} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line type="monotone" dataKey="pv" name="PV" stroke="#F59E0B" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="consumption" name="Consumption" stroke="#FAFAFA" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="import" name="Import" stroke="#E11D48" strokeWidth={1.5} dot={false} />
              <Line type="monotone" dataKey="export" name="Export" stroke="#10B981" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Monthly chart */}
      <section className="card-base p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="label-tiny">Monthly totals — last 12 months</div>
          <div className="flex gap-3 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#E11D48]"/>Import</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#10B981]"/>Export</span>
            {monthlyRows.some((m) => m.pv > 0) && (
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#F59E0B]"/>PV</span>
            )}
          </div>
        </div>
        <div className="h-72" data-testid="energy-monthly-chart">
          <ResponsiveContainer>
            <BarChart data={monthlyRows} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#27272A" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={{ stroke: "#27272A" }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="import" name="Import" fill="#E11D48" radius={[3, 3, 0, 0]} />
              <Bar dataKey="export" name="Export" fill="#10B981" radius={[3, 3, 0, 0]} />
              {monthlyRows.some((m) => m.pv > 0) && (
                <Bar dataKey="pv" name="PV" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
        {monthlyRows.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-6" data-testid="energy-monthly-empty">
            Not enough history yet — monthly totals will appear after a few weeks of data.
          </div>
        )}
      </section>

      {/* Daily log table */}
      <section className="card-base overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="label-tiny">Daily log · {rows.length} rows</div>
          <ConfidencePill level="measured" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="energy-table">
            <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-5 py-2.5">Date</th>
                <th className="text-right px-3 py-2.5">PV</th>
                <th className="text-right px-3 py-2.5">Import</th>
                <th className="text-right px-3 py-2.5">Export</th>
                <th className="text-right px-3 py-2.5">Consumption</th>
                <th className="text-right px-3 py-2.5">Self-cons</th>
                <th className="text-right px-5 py-2.5">Autonomy</th>
              </tr>
            </thead>
            <tbody className="font-mono text-[12px]">
              {[...rows].reverse().slice(0, 30).map((r) => (
                <tr key={r.date} className="border-b border-border/60 hover:bg-secondary/30">
                  <td className="px-5 py-2 font-sans">{shortDate(r.date)}</td>
                  <td className="text-right px-3 text-[#F59E0B]">{fmt(r.pv_production_kwh, 1)}</td>
                  <td className="text-right px-3 text-[#E11D48]">{fmt(r.grid_import_kwh, 1)}</td>
                  <td className="text-right px-3 text-[#10B981]">{fmt(r.grid_export_kwh, 1)}</td>
                  <td className="text-right px-3">{fmt(r.site_consumption_kwh, 1)}</td>
                  <td className="text-right px-3 text-muted-foreground">{fmt(r.self_consumption_pct, 0)}%</td>
                  <td className="text-right px-5 text-muted-foreground">{fmt(r.grid_autonomy_pct, 0)}%</td>
                </tr>
              ))}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No energy data yet — connect the HomeWizard bridge or import a smart-meter CSV.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <CsvImportModal
        open={openImport === "p1"}
        onClose={() => setOpenImport(null)}
        onSuccess={reload}
        title="Import HomeWizard P1 CSV"
        description="Auto-detects daily vs cumulative readings. Cumulative is converted to daily deltas."
        endpoint={`/sites/${currentSite?.id}/imports/p1`}
      />
      <CsvImportModal
        open={openImport === "pv"}
        onClose={() => setOpenImport(null)}
        onSuccess={reload}
        title="Import FusionSolar PV CSV"
        description="Daily PV yield. Existing grid import/export will not be overwritten."
        endpoint={`/sites/${currentSite?.id}/imports/pv`}
      />
    </div>
  );
}
